import { useState, useEffect, useCallback, useMemo } from 'react'
import api from '../../../api/client'
import { emitSubscriptionActivated } from '../../../utils/permissionDeniedEvents'

export function useSubscriptions() {
  const [data, setData] = useState({
    active_modules: [],
    subscriptions: [],
    available_trials: [],
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)

  const fetchStatus = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get('/api/subscriptions/status')
      const payload = res.data || { active_modules: [], subscriptions: [], available_trials: [] }
      setData(payload)
      if (Array.isArray(payload.active_modules) && payload.active_modules.length > 0) {
        emitSubscriptionActivated(payload.active_modules)
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to load subscription status.'
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  const activateTrial = async (itemType, itemCode) => {
    setActionLoading(true)
    setError(null)
    try {
      const res = await api.post('/api/subscriptions/trial', {
        item_type: itemType,
        item_code: itemCode,
      })
      if (res.data?.data) {
        setData(res.data.data)
        const updatedModules = res.data.data.active_modules || []
        if (updatedModules.length > 0) {
          emitSubscriptionActivated(updatedModules)
        }
      } else {
        await fetchStatus()
      }
      return { success: true, message: res.data?.message }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to activate trial.'
      const errorText = typeof msg === 'string' ? msg : JSON.stringify(msg)
      setError(errorText)
      return { success: false, error: errorText }
    } finally {
      setActionLoading(false)
    }
  }

  // Derive coverage map: which individual modules are covered by active subscription packs
  const coverageMap = useMemo(() => {
    const map = {}
    const activePacks = (data.subscriptions || []).filter(
      (s) => s.item_type === 'PACK' && (s.status === 'ACTIVE' || s.status === 'TRIALING')
    )
    const packModDict = {
      standard: ['bridge', 'bruno'],
      premium: ['bridge', 'nerve'],
      pro: ['bridge', 'nerve', 'bruno'],
      enterprise: ['bridge', 'nerve', 'bruno', 'telephony'],
      developer: ['bridge', 'nerve', 'bruno', 'telephony'],
    }
    activePacks.forEach((p) => {
      const code = String(p.item_code || '').toLowerCase()
      const mods = packModDict[code] || []
      mods.forEach((m) => {
        if (!map[m]) map[m] = p.item_name || code
      })
    })
    return map
  }, [data.subscriptions])

  return {
    activeModules: data.active_modules || [],
    subscriptions: data.subscriptions || [],
    availableTrials: data.available_trials || [],
    coverageMap,
    loading,
    error,
    actionLoading,
    activateTrial,
    refresh: fetchStatus,
  }
}

export default useSubscriptions
